import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import BlogCTAButtons from "@/components/BlogCTAButtons";
import CanonicalTag from "@/components/CanonicalTag";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import heroImage from "@/assets/indoor-outdoor-adu-living.png";

const BlogIndoorOutdoorADUValue = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Indoor Outdoor ADU Living That Sells: Why This Design Increases Use and Value",
    "description": "Folding doors, seamless transitions, and usable patios. Learn why indoor outdoor ADU design increases demand, rental value, and resale appeal.",
    "datePublished": "2026-04-15",
    "author": { "@type": "Organization", "name": "UDA ADU" },
    "publisher": { "@type": "Organization", "name": "UDA ADU" },
  };

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/blogs/indoor-outdoor-adu-value" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <Navigation />
      <div className="w-full max-w-5xl mx-auto px-4 pt-8">
        <img src={heroImage} alt="Indoor outdoor ADU living with seamless transition" className="w-full rounded-2xl" />
      </div>
      <main className="max-w-3xl mx-auto px-4 py-12">
        <Link to="/blogs" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Blog
        </Link>
        <p className="text-sm text-muted-foreground mb-2">April 15, 2026</p>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">Indoor Outdoor ADU Living That Sells: Why This Design Increases Use and Value</h1>
        <article className="prose prose-lg max-w-none text-muted-foreground space-y-6">
          <p>Most ADUs are built as separate spaces.</p>
          <p>They work. They check the box. But they don't stand out.</p>
          <p>The ADUs that get more attention, higher rent, and stronger long term value all share one thing. They feel open. They feel connected.</p>
          <p>Indoor outdoor living is a big part of that.</p>
          <p>At UDA ADU, this is not treated as a feature. It is part of the core design.</p>
          <p>Large openings are the foundation.</p>
          <p>Folding or sliding glass doors remove the barrier between inside and outside. When fully open, the living area extends into the patio. The unit instantly feels larger without adding square footage.</p>
          <p>This is not visual only.</p>
          <p>It changes how people use the space every day. Eating outside becomes easier. Hosting feels natural. Even quiet time feels different when the space is open.</p>
          <p>Seamless transition is what makes it work.</p>
          <p>Floor levels, material tones, and door placement all need to align. If the transition feels forced, the concept breaks. When done right, you do not notice where inside ends and outside begins.</p>
          <p>Outdoor space needs to be usable.</p>
          <p>A small patio with proper layout can outperform a larger one that is not planned well. Seating, shade, and access all matter. The goal is simple. Make the space easy to step into and actually use.</p>
          <p>Light ties everything together.</p>
          <p>Natural light moves through the entire unit when openings are placed correctly. It reaches deeper into the kitchen and living areas. The space feels active, not closed.</p>
          <p>Privacy still stays controlled.</p>
          <p>Orientation, fencing, and landscaping keep the space open without exposing it. This balance is what separates a good design from one that looks good only in photos.</p>
          <p>This approach works across different ADU uses.</p>
          <p>For rentals, it increases demand and perceived value. For family use, it creates comfort and flexibility. For resale, it adds a feature buyers recognize immediately.</p>
          <p>In California, where outdoor living is part of daily life, this design is not optional anymore.</p>
          <p>It is expected.</p>
          <p>The difference is how well it is executed.</p>
        </article>
        <BlogCTAButtons />
      </main>
      <FloatingADUWizard />
      <FloatingReserveButton />
      <Footer />
    </div>
  );
};

export default BlogIndoorOutdoorADUValue;
